
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { createJob, getJobs, deleteJob } from "@/utils/jobHelpers";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, RefreshCw, AlertCircle, Camera, Tag, Database, Filter } from "lucide-react";
import CreateJobModal from "@/components/admin/modals/CreateJobModal";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { formatDistance } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Jobs = () => {
  const [isCreatingJob, setIsCreatingJob] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [jobs, setJobs] = useState<any[]>([]);
  const [expandedJobs, setExpandedJobs] = useState<string[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState<string>("");
  const [filterType, setFilterType] = useState<string>("all");
  const [jobType, setJobType] = useState<string>("EXTRACT_METRICS_BATCH");
  const { toast } = useToast();
  
  // Job type options
  const jobTypes = [
    { value: "EXTRACT_METRICS_BATCH", label: "Extract Metrics", icon: <Database className="h-4 w-4 mr-2" /> },
    { value: "EXTRACT_TAGS", label: "Extract Tags", icon: <Tag className="h-4 w-4 mr-2" /> },
    { value: "SCREENSHOT", label: "Take Screenshot", icon: <Camera className="h-4 w-4 mr-2" /> }
  ];

  const fetchJobs = async () => {
    // Only set loading state on initial load, not on background refreshes
    const isInitialLoad = jobs.length === 0;
    if (isInitialLoad) {
      setIsLoading(true);
    }
    
    try {
      console.log('Fetching jobs...');
      const { data, error } = await getJobs();

      if (error) {
        console.error('Error fetching jobs:', error);
        
        // Only show toast on initial load or significant errors
        if (isInitialLoad) {
          toast({
            variant: "destructive",
            title: "Error fetching jobs",
            description: error.message || "Failed to fetch jobs"
          });
        }
        
        // Don't clear existing jobs on refresh errors
        if (isInitialLoad) {
          setJobs([]);
        }
        return;
      }

      console.log(`Fetched ${data?.length || 0} jobs`);
      
      // Only update state if we got data or if it's the initial load
      if (data || isInitialLoad) {
        setJobs(data || []);
      }
    } catch (error: any) {
      console.error('Error fetching jobs:', error);
      
      // Only show toast on initial load or significant errors
      if (isInitialLoad) {
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message || "Failed to fetch jobs"
        });
        setJobs([]);
      }
    } finally {
      // Ensure loading state is always turned off for initial loads
      if (isInitialLoad) {
        setTimeout(() => {
          setIsLoading(false);
        }, 500); // Small delay to ensure state updates properly
      }
    }
  };

  // Function to check the status of pending screenshot jobs
  const checkPendingScreenshotJobs = async () => {
    const pendingScreenshotJobs = jobs.filter(job => 
      job.spec?.type === "SCREENSHOT" && 
      job.status === "pending"
    );
    
    if (pendingScreenshotJobs.length === 0) return;
    
    console.log(`Checking status of ${pendingScreenshotJobs.length} pending screenshot jobs`);
    
    for (const job of pendingScreenshotJobs) {
      const slug = job.spec?.data?.slug;
      if (!slug) continue;
      
      try {
        // Check the scrapes table for the latest screenshot
        const { data, error } = await supabase
          .from('scrapes')
          .select('*')
          .eq('slug', slug)
          .order('timestamp', { ascending: false })
          .limit(1);
        
        if (error) {
          console.error(`Error checking scrape status for ${slug}:`, error);
          continue;
        }
        
        if (data && data.length > 0) {
          const scrape = data[0];
          const scrapeTimestamp = new Date(scrape.timestamp);
          const jobTimestamp = new Date(job.timestamp || job.created_at);
          
          // If the scrape is newer than the job and has a status
          if (scrapeTimestamp > jobTimestamp) {
            console.log(`Found newer scrape for ${slug} with status: ${scrape.status}`);
            
            // Map scrape status to job status
            let jobStatus = "pending";
            if (scrape.status === "completed") jobStatus = "done";
            else if (scrape.status === "failed") jobStatus = "failed";
            else if (scrape.status === "processing") jobStatus = "running";
            
            // Update the job status
            if (jobStatus !== job.status) {
              const { error: updateError } = await supabase
                .from('slug_jobs')
                .update({
                  status: jobStatus,
                  result: {
                    screenshot_url: scrape.screenshot_url,
                    cloudinary_url: scrape.cloudinary_url,
                    version: scrape.version,
                    last_error: scrape.last_error
                  },
                  ...(jobStatus === "done" ? { last_successful_timestamp: new Date().toISOString() } : {})
                })
                .eq('id', job.id);
              
              if (updateError) {
                console.error(`Error updating job status for ${slug}:`, updateError);
              } else {
                console.log(`Updated job status for ${slug} to ${jobStatus}`);
                // Refresh jobs after updating status
                fetchJobs();
              }
            }
          }
        }
      } catch (error) {
        console.error(`Error checking scrape status for ${slug}:`, error);
      }
    }
  };

  // Set up initial data loading and polling
  useEffect(() => {
    console.log('Setting up initial data loading and polling');
    
    // Initial data loading
    const loadInitialData = async () => {
      try {
        await fetchJobs();
        console.log('Initial data loaded successfully');
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    
    loadInitialData();
    
    // Set up an interval to refresh jobs
    const refreshInterval = setInterval(() => {
      console.log('Auto-refreshing jobs...');
      fetchJobs();
    }, 15000); // Refresh every 15 seconds
    
    // Set up an interval to check pending screenshot jobs
    const checkInterval = setInterval(() => {
      const hasPendingScreenshotJobs = jobs.some(
        job => job.spec?.type === "SCREENSHOT" && job.status === "pending"
      );
      
      if (hasPendingScreenshotJobs) {
        console.log('Found pending screenshot jobs, checking status...');
        checkPendingScreenshotJobs();
      }
    }, 5000); // Check every 5 seconds
    
    // Cleanup function
    return () => {
      console.log('Cleaning up intervals');
      clearInterval(refreshInterval);
      clearInterval(checkInterval);
    };
  }, []); // Empty dependency array for initial setup only

  const handleCreateJob = async (jobSpec: any) => {
    setIsCreatingJob(true);
    try {
      const { data, error } = await createJob(jobSpec);

      if (error) {
        toast({
          variant: "destructive",
          title: "Error creating job",
          description: error.message || "Failed to create new job"
        });
        return;
      }

      toast({
        title: "Job created",
        description: "Successfully created and queued new job"
      });

      fetchJobs();
    } catch (error: any) {
      console.error('Error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to create job"
      });
    } finally {
      setIsCreatingJob(false);
    }
  };
  
  // Function to create a job for a specific slug
  const createJobForSlug = async (slug: string, type: string = jobType) => {
    setSelectedSlug(slug);
    setIsCreatingJob(true);
    
    try {
      // For screenshot jobs, call the screenshot-page function directly
      if (type === "SCREENSHOT") {
        console.log('Creating screenshot job for', slug);
        
        // First check if the company exists
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('slug')
          .eq('slug', slug)
          .maybeSingle();
        
        if (companyError) {
          throw new Error(`Error checking company: ${companyError.message}`);
        }
        
        if (!companyData) {
          throw new Error(`Company with slug "${slug}" not found`);
        }
        
        // Call the screenshot-page function
        const response = await supabase.functions.invoke('screenshot-page', {
          body: {
            slug,
            force: true,
            useAI: true
          }
        });
        
        if (response.error) {
          throw new Error(response.error.message || 'Failed to create screenshot job');
        }
        
        toast({
          title: "Screenshot job created",
          description: `Screenshot job for ${slug} has been initiated`
        });
        
        // Refresh the jobs list immediately and again after a delay
        fetchJobs();
        setTimeout(() => {
          fetchJobs();
        }, 2000);
      } else {
        // For other job types, use the regular job creation flow
        const jobSpec: any = {
          type: type,
          data: {
            slug: slug,
            force: true
          }
        };
        
        // Add specific configurations based on job type
        if (type === "EXTRACT_TAGS") {
          jobSpec.data.forceReprocess = true;
        }
        
        handleCreateJob(jobSpec);
      }
    } catch (error: any) {
      console.error('Error creating job:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to create job"
      });
    } finally {
      setIsCreatingJob(false);
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    try {
      const { error } = await deleteJob(jobId);

      if (error) {
        toast({
          variant: "destructive",
          title: "Error deleting job",
          description: error.message
        });
        return;
      }

      toast({
        title: "Job deleted",
        description: "Successfully deleted job and associated tasks"
      });

      fetchJobs();
    } catch (error: any) {
      console.error('Error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete job"
      });
    }
  };

  const toggleJobExpansion = (jobId: string) => {
    setExpandedJobs(prev =>
      prev.includes(jobId)
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  const copyToClipboard = async (data: any) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast({
        title: "Copied to clipboard",
        description: "Results have been copied to your clipboard"
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Failed to copy",
        description: "Could not copy results to clipboard"
      });
    }
  };

  // Get filtered jobs based on the selected filter
  const filteredJobs = jobs.filter(job => {
    if (filterType === "all") return true;
    
    // Get the job type from the job spec
    const jobType = job.spec?.type || "";
    return jobType.includes(filterType);
  });
  
  // Format job status for display
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "done":
        return <Badge className="bg-green-500">Done</Badge>;
      case "failed":
        return <Badge variant="destructive">Failed</Badge>;
      case "running":
        return <Badge className="bg-blue-500">Running</Badge>;
      case "pending":
        return <Badge variant="outline">Pending</Badge>;
      case "timed_out":
        return <Badge variant="destructive">Timed Out</Badge>;
      default:
        return <Badge variant="secondary">{status || "Unknown"}</Badge>;
    }
  };
  
  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return formatDistance(date, new Date(), { addSuffix: true });
    } catch (error) {
      return "Unknown date";
    }
  };
  
  return (
    <div className="container max-w-[1400px] mx-auto p-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Jobs</h1>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            className="flex items-center gap-1"
            onClick={fetchJobs}
          >
            <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
            {isLoading ? "Loading..." : "Refresh"}
          </Button>
        </div>
      </div>

      <Card className="p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Job Management</h2>
        <p className="text-gray-600 mb-4">
          Manage pricing data extraction jobs across different companies. 
          You can trigger new metric extraction, tag extraction, or page scraping jobs.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Company Slug
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter company slug"
              value={selectedSlug}
              onChange={(e) => setSelectedSlug(e.target.value)}
            />
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Job Type
            </label>
            <Select value={jobType} onValueChange={setJobType}>
              <SelectTrigger>
                <SelectValue placeholder="Select job type" />
              </SelectTrigger>
              <SelectContent>
                {jobTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center">
                      {type.icon}
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-end">
            <Button 
              variant="default"
              className="flex items-center gap-1 w-full"
              onClick={() => {
                if (selectedSlug.trim()) {
                  createJobForSlug(selectedSlug.trim());
                } else {
                  toast({
                    variant: "destructive",
                    title: "Error",
                    description: "Please enter a company slug"
                  });
                }
              }}
              disabled={isCreatingJob || !selectedSlug.trim()}
            >
              {isCreatingJob ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus size={16} className="mr-1" />
                  Create Job
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Jobs List</h2>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center gap-1">
                <Filter size={16} />
                Filter: {filterType === "all" ? "All Types" : 
                  jobTypes.find(t => t.value.includes(filterType))?.label || filterType}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setFilterType("all")}>
                All Types
              </DropdownMenuItem>
              {jobTypes.map(type => (
                <DropdownMenuItem 
                  key={type.value} 
                  onClick={() => setFilterType(type.value)}
                  className="flex items-center"
                >
                  {type.icon}
                  {type.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-2 text-lg">Loading jobs...</span>
        </div>
      ) : jobs.length === 0 ? (
        <Alert className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No jobs found</AlertTitle>
          <AlertDescription>
            No jobs were found in the system. Create a new job to get started.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-6 text-gray-500">
                    No jobs match the current filter
                  </TableCell>
                </TableRow>
              ) : (
                filteredJobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell className="font-mono text-xs">
                      {job.id.substring(0, 8)}...
                    </TableCell>
                    <TableCell>
                      {job.spec?.data?.slug || "N/A"}
                    </TableCell>
                    <TableCell>
                      {job.spec?.type === "EXTRACT_METRICS_BATCH" && (
                        <Badge variant="outline" className="flex items-center gap-1 w-fit">
                          <Database className="h-3 w-3" />
                          Metrics
                        </Badge>
                      )}
                      {job.spec?.type === "EXTRACT_TAGS" && (
                        <Badge variant="outline" className="flex items-center gap-1 w-fit">
                          <Tag className="h-3 w-3" />
                          Tags
                        </Badge>
                      )}
                      {job.spec?.type === "SCREENSHOT" && (
                        <Badge variant="outline" className="flex items-center gap-1 w-fit">
                          <Camera className="h-3 w-3" />
                          Screenshot
                        </Badge>
                      )}
                      {!["EXTRACT_METRICS_BATCH", "EXTRACT_TAGS", "SCREENSHOT"].includes(job.spec?.type) && (
                        <Badge variant="outline" className="w-fit">
                          {job.spec?.type || "Unknown"}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(job.status)}
                    </TableCell>
                    <TableCell>
                      {formatDate(job.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteJob(job.id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <CreateJobModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateJob}
      />
    </div>
  );
};

export default Jobs;
